import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, Filter, Settings, Layers, Play } from 'lucide-react';
import QuestionCard from './QuestionCard';
import BoxDisplay from './BoxDisplay';
import CategoryFilter from './CategoryFilter';
import { leitnerService } from '../../services/leitnerService';

const Day = {
  Monday: 'Monday',
  Tuesday: 'Tuesday',
  Wednesday: 'Wednesday',
  Thursday: 'Thursday',
  Friday: 'Friday',
  Saturday: 'Saturday',
  Sunday: 'Sunday'
};

const LearnerView = ({ bookId, questions, updateLearnerProgress, styling }) => {
  const today = new Date().toLocaleString('en-US', { weekday: 'long' });
  const [selectedDay, setSelectedDay] = useState(today);
  const [sessionQuestions, setSessionQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sessionState, setSessionState] = useState('idle');
  
  const [timerMode, setTimerMode] = useState('none');
  const [timePerQuestion, setTimePerQuestion] = useState(60);
  const [timeForSession, setTimeForSession] = useState(300);
  const [timeLeft, setTimeLeft] = useState(0);
  const [finishReason, setFinishReason] = useState('completed');

  const [categories, setCategories] = useState({ subjects: [], grades: [], topics: [] });
  const [filters, setFilters] = useState({ subject: null, grade: null, topics: [] });
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  // Collapsible panel states
  const [showFilters, setShowFilters] = useState(true);
  const [showSessionSetup, setShowSessionSetup] = useState(true);
  const [showBoxDisplay, setShowBoxDisplay] = useState(true);

  // Auto-collapse panels when session starts
  useEffect(() => {
    if (sessionState === 'active') {
      setShowFilters(false);
      setShowSessionSetup(false);
      setShowBoxDisplay(false);
    }
  }, [sessionState]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await leitnerService.getQuestionCategories(bookId);
        setCategories(cats);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    
    if (bookId) {
      loadCategories();
    }
  }, [bookId]);

  useEffect(() => {
    const filtered = leitnerService.filterQuestionsByCategories(questions, filters);
    setFilteredQuestions(filtered);
  }, [questions, filters]);

  // Flatten questions: simple questions and individual subquestions
  const flattenedQuestions = useMemo(() => {
    const flat = [];
    const publishedQuestions = filteredQuestions.filter(q => q.status === 'published');
    
    publishedQuestions.forEach(q => {
      if (q.type === 'simple') {
        flat.push({ ...q, isStem: false });
      } else if (q.type === 'stem' && q.subQuestions) {
        q.subQuestions.forEach(subQ => {
          flat.push({
            ...subQ,
            isStem: true,
            stemId: q.id,
            stemText: q.stemText,
            stemImage: q.stemImage,
            stemMedia: q.media
          });
        });
      }
    });
    return flat;
  }, [filteredQuestions]);

  const getQueueForDay = useCallback((day) => {
    if (day === Day.Friday) return [3, 2, 1];
    if (day === Day.Tuesday || day === Day.Thursday) return [2, 1];
    return [1];
  }, []);

  const startSession = () => {
    const queue = getQueueForDay(selectedDay);
    const questionsForSession = flattenedQuestions
      .filter(q => queue.includes(q.box || 1))
      .sort((a, b) => (b.box || 1) - (a.box || 1));

    if (questionsForSession.length === 0) {
      alert('No questions in the queue for this day.');
      return;
    }

    setSessionQuestions(questionsForSession);
    setCurrentIndex(0);
    setSessionState('active');
    setFinishReason('completed');

    if (timerMode === 'perQuestion') setTimeLeft(timePerQuestion);
    else if (timerMode === 'perSession') setTimeLeft(timeForSession);
  };

  useEffect(() => {
    if (sessionState !== 'active' || timerMode === 'none') return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (timerMode === 'perQuestion') {
            if (currentIndex < sessionQuestions.length - 1) {
              setCurrentIndex(i => i + 1);
              return timePerQuestion;
            } else {
              setFinishReason('completed');
              setSessionState('finished');
              return 0;
            }
          } else {
            setFinishReason('timedOut');
            setSessionState('finished');
            return 0;
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [sessionState, timerMode, currentIndex, sessionQuestions.length, timePerQuestion]);

  const handleAnswerSubmit = async (isCorrect) => {
    const q = sessionQuestions[currentIndex];
    const isSubQuestion = q.isStem;
    const parentQuestionId = isSubQuestion ? q.stemId : null;

    try {
      await updateLearnerProgress(q.id, isCorrect, isSubQuestion, parentQuestionId);
    } catch (error) {
      console.error('Failed to update progress:', error);
    }

    if (currentIndex < sessionQuestions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      if (timerMode === 'perQuestion') setTimeLeft(timePerQuestion);
    } else {
      setFinishReason('completed');
      setSessionState('finished');
    }
  };

  const hasActiveFilters = filters.subject || filters.grade || filters.topics.length > 0;
  const currentQuestion = sessionState === 'active' && sessionQuestions.length > 0 ? sessionQuestions[currentIndex] : null;

  const CollapsibleHeader = ({ title, icon: Icon, isOpen, onToggle, badge }) => (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-3 rounded-lg transition-colors"
      style={{ backgroundColor: styling?.headerColor || '#1e293b' }}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-indigo-400" />
        <span className="font-medium text-slate-200">{title}</span>
        {badge && (
          <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full">{badge}</span>
        )}
      </div>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-slate-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400" />
      )}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Collapsible Filters */}
      <div className="rounded-lg overflow-hidden" style={{ backgroundColor: styling?.headerColor || '#1e293b' }}>
        <CollapsibleHeader
          title="Filters"
          icon={Filter}
          isOpen={showFilters}
          onToggle={() => setShowFilters(!showFilters)}
          badge={hasActiveFilters ? 'Active' : null}
        />
        {showFilters && (
          <div className="p-4 border-t border-slate-700">
            <CategoryFilter
              categories={categories}
              selectedFilters={filters}
              onFilterChange={setFilters}
              onClear={() => setFilters({ subject: null, grade: null, topics: [] })}
              questions={questions}
            />
          </div>
        )}
      </div>

      {/* Session Setup & Box Display Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Collapsible Session Setup */}
        <div className="md:col-span-1 rounded-lg overflow-hidden" style={{ backgroundColor: styling?.headerColor || '#1e293b' }}>
          <CollapsibleHeader
            title="Session Setup"
            icon={Settings}
            isOpen={showSessionSetup}
            onToggle={() => setShowSessionSetup(!showSessionSetup)}
          />
          {showSessionSetup && (
            <div className="p-4 border-t border-slate-700 space-y-4">
              <div>
                <label htmlFor="day-select" className="block text-sm font-medium text-slate-300 mb-2">
                  Select Study Day
                </label>
                <select 
                  id="day-select" 
                  value={selectedDay} 
                  onChange={(e) => setSelectedDay(e.target.value)} 
                  disabled={sessionState === 'active'} 
                  className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-slate-100 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {Object.values(Day).map(day => <option key={day} value={day}>{day}</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-2">
                  {selectedDay === Day.Friday && "Today's queue: Box 3 → 2 → 1"}
                  {(selectedDay === Day.Tuesday || selectedDay === Day.Thursday) && "Today's queue: Box 2 → 1"}
                  {![Day.Friday, Day.Tuesday, Day.Thursday].includes(selectedDay) && "Today's queue: Box 1"}
                </p>
              </div>

              <div className="border-t border-slate-700 pt-4">
                <h3 className="text-sm font-medium text-slate-300 mb-3">Timer Settings</h3>
                <div className="space-y-3">
                  <select 
                    value={timerMode} 
                    onChange={e => setTimerMode(e.target.value)} 
                    disabled={sessionState === 'active'} 
                    className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    <option value="none">No Timer</option>
                    <option value="perQuestion">Per Question</option>
                    <option value="perSession">Entire Session</option>
                  </select>
                  
                  {timerMode === 'perQuestion' && (
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Seconds per question</label>
                      <input 
                        type="number" 
                        value={timePerQuestion} 
                        onChange={e => setTimePerQuestion(Math.max(5, Number(e.target.value)))} 
                        min="5" 
                        className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-slate-100 disabled:opacity-50" 
                        disabled={sessionState === 'active'} 
                      />
                    </div>
                  )}
                  
                  {timerMode === 'perSession' && (
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Total minutes</label>
                      <input 
                        type="number" 
                        value={timeForSession / 60} 
                        onChange={e => setTimeForSession(Math.max(1, Number(e.target.value)) * 60)} 
                        min="1" 
                        className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-slate-100 disabled:opacity-50" 
                        disabled={sessionState === 'active'} 
                      />
                    </div>
                  )}
                </div>
              </div>

              {sessionState === 'idle' && (
                <button 
                  onClick={startSession} 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Start Session
                </button>
              )}
            </div>
          )}
        </div>

        {/* Collapsible Box Display */}
        <div className="md:col-span-2 rounded-lg overflow-hidden" style={{ backgroundColor: styling?.headerColor || '#1e293b' }}>
          <CollapsibleHeader
            title="Box Distribution"
            icon={Layers}
            isOpen={showBoxDisplay}
            onToggle={() => setShowBoxDisplay(!showBoxDisplay)}
            badge={flattenedQuestions.length}
          />
          {showBoxDisplay && (
            <div className="p-4 border-t border-slate-700">
              <BoxDisplay 
                questions={flattenedQuestions}
                styling={styling}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Question Area */}
      <div className="bg-slate-800 p-4 rounded-lg shadow-xl min-h-[400px] flex flex-col items-center justify-center">
        {sessionState === 'idle' && (
          <div className="text-center">
            <h3 className="text-2xl font-semibold text-slate-200">Ready to learn?</h3>
            <p className="text-slate-400 mt-2 mb-6">Configure your session above and click Start.</p>
            <button 
              onClick={startSession} 
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-md transition-transform transform hover:scale-105 shadow-lg inline-flex items-center gap-2"
            >
              <Play className="w-5 h-5" />
              Start Session for {selectedDay}
            </button>
          </div>
        )}

        {sessionState === 'active' && currentQuestion && (
          <div className="w-full max-w-3xl">
            {/* Compact Header when in session */}
            <div className="flex justify-between items-center text-slate-400 mb-4 px-2">
              <div className="flex items-center gap-4">
                <p className="text-sm">Q {currentIndex + 1}/{sessionQuestions.length}</p>
                <span className="px-2 py-1 bg-slate-700 rounded text-xs">Box {currentQuestion.box}</span>
              </div>
              <div className="flex items-center gap-3">
                {timerMode !== 'none' && (
                  <div className={`font-bold text-lg tabular-nums rounded-md px-3 py-1 ${timeLeft <= 10 && timerMode === 'perQuestion' ? 'text-red-400 bg-red-900/30 animate-pulse' : 'text-slate-300 bg-slate-700'}`}>
                    {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:{String(timeLeft % 60).padStart(2, '0')}
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowFilters(false);
                    setShowSessionSetup(!showSessionSetup);
                    setShowBoxDisplay(false);
                  }}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  title="Toggle Setup"
                >
                  <Settings className="w-4 h-4 text-slate-400" />
                </button>
                <button
                  onClick={() => {
                    setShowFilters(false);
                    setShowSessionSetup(false);
                    setShowBoxDisplay(!showBoxDisplay);
                  }}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  title="Toggle Boxes"
                >
                  <Layers className="w-4 h-4 text-slate-400" />
                </button>
              </div>
            </div>
            <QuestionCard
              key={currentQuestion.id}
              question={currentQuestion}
              onSubmit={handleAnswerSubmit}
              stemText={currentQuestion.isStem ? currentQuestion.stemText : undefined}
              stemImage={currentQuestion.isStem ? currentQuestion.stemImage : undefined}
              stemMedia={currentQuestion.isStem ? currentQuestion.stemMedia : undefined}
            />
          </div>
        )}

        {sessionState === 'finished' && (
          <div className="text-center">
            {finishReason === 'timedOut' ? (
              <>
                <h3 className="text-2xl font-semibold text-amber-400">Time's Up!</h3>
                <p className="text-slate-300 mt-2 mb-6">The session timer has run out.</p>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-semibold text-green-400">Session Complete!</h3>
                <p className="text-slate-300 mt-2 mb-6">Great work! You've reviewed all scheduled questions for today.</p>
              </>
            )}
            <button 
              onClick={() => {
                setSessionState('idle');
                setShowSessionSetup(true);
                setShowBoxDisplay(true);
              }} 
              className="bg-slate-600 hover:bg-slate-700 text-white font-bold py-3 px-8 rounded-md transition"
            >
              Back to Setup
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearnerView;